/**
 * FL-246: ドライバースケジュール管理コンポーネント（管理者向け）
 *
 * 複数週・複数ドライバーのスケジュール管理・承認をするメインコンポーネント
 * フィルター、表示、承認パネルを統合
 *
 * TDD Red-Green-Refactorサイクルに従った実装
 */

import React, { useState, useEffect } from 'react';
import { DriverSchedule } from '@/types/schedule';
import { ScheduleFilterPanel, ScheduleFilter } from './ScheduleFilterPanel';
import { MultiWeekScheduleDisplay } from './MultiWeekScheduleDisplay';
import { ScheduleApprovalPanel } from './ScheduleApprovalPanel';

interface Driver {
  id: string;
  name: string;
}

/**
 * 日付範囲フィルタで無制限を表現するための定数
 * getCompanySchedules() で全期間取得するため、フロントエンドでも
 * 全期間表示可能にする際に使用
 */
const UNLIMITED_DATE_RANGE = {
  start: '1900-01-01',
  end: '2099-12-31',
} as const;

interface DriverScheduleManagerProps {
  /** 表示日付（この日付を含む週を表示） */
  selectedDate?: string;
  /** スケジュール一覧 */
  schedules: DriverSchedule[];
  /** ドライバー一覧 */
  drivers: Driver[];
  /** スケジュール更新時のコールバック */
  onScheduleUpdate: (scheduleId: string, updates: Partial<DriverSchedule>) => void;
  /** 一括承認時のコールバック */
  onBatchApprove: (scheduleIds: string[]) => void;
  /** 一括キャンセル時のコールバック */
  onBatchCancel: (scheduleIds: string[], reason: string) => void;
}

/**
 * 現在週の月曜日を取得
 */
const getCurrentWeekStart = (): string => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1); // 月曜日に調整
  const monday = new Date(now.setDate(diff));
  return monday.toISOString().split('T')[0];
};

/**
 * 指定日付を含む週の月曜日を取得
 * @param date - 指定日付（YYYY-MM-DD形式）
 * @returns 週の月曜日（YYYY-MM-DD形式）
 */
const getWeekStartFromDate = (date: string): string => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 月曜日に調整
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().split('T')[0];
};

/**
 * DriverScheduleManager コンポーネント
 *
 * @param companyId - 会社ID
 * @param schedules - スケジュール一覧
 * @param drivers - ドライバー一覧
 * @param onScheduleUpdate - スケジュール更新コールバック
 * @param onScheduleDelete - スケジュール削除コールバック
 * @param onBatchApprove - 一括承認コールバック
 * @param onBatchCancel - 一括キャンセルコールバック
 * @returns JSX要素
 */
export const DriverScheduleManager: React.FC<DriverScheduleManagerProps> = ({
  selectedDate,
  schedules,
  drivers,
  onScheduleUpdate,
  onBatchApprove,
  onBatchCancel,
}) => {
  // 表示週の管理：selectedDateが指定されている場合はそれを基準に、なければ現在週を基準に
  const [weekStart, setWeekStart] = useState<string>(
    selectedDate ? getWeekStartFromDate(selectedDate) : getCurrentWeekStart()
  );
  const weekCount = 2;

  // フィルター条件
  // 修正: 日付範囲フィルを削除（getCompanySchedules で全期間取得するため、
  //      フロントエンドでも全期間表示可能にする）
  const [filter, setFilter] = useState<ScheduleFilter>({
    driverIds: drivers.map(d => d.id),
    statuses: ['registered', 'confirmed', 'cancelled'],
    dateRange: UNLIMITED_DATE_RANGE,
  });

  /**
   * selectedDateが変更された場合、weekStartを更新
   * 修正: dateRange フィルタは更新しない（全期間表示のため）
   */
  useEffect(() => {
    if (selectedDate) {
      const newWeekStart = getWeekStartFromDate(selectedDate);
      setWeekStart(newWeekStart);
      // dateRange フィルタは更新しない（全期間表示）
    }
  }, [selectedDate]);

  /**
   * 🔧 修正: driversプロップが変更された場合、filter.driverIdsを更新する
   *
   * 問題: コンポーネント初期化時に drivers = [] （初期状態）の場合、
   *      filter.driverIds = [] になり、その後 drivers データが到着しても
   *      driverIds が更新されない
   *
   * 解決: drivers が変更されるたびに filter.driverIds を更新する
   */
  useEffect(() => {
    if (drivers.length > 0) {
      setFilter(prev => ({
        ...prev,
        driverIds: drivers.map(d => d.id),
      }));
    }
  }, [drivers]);

  /**
   * フィルター適用済みスケジュールを計算
   */
  const filteredSchedules = schedules.filter(schedule => {
    // ドライバーフィルター
    if (filter.driverIds.length > 0 && !filter.driverIds.includes(schedule.landTransporterId)) {
      return false;
    }

    // ステータスフィルター
    if (filter.statuses.length > 0 && !filter.statuses.includes(schedule.status)) {
      return false;
    }

    // 日付範囲フィルター
    if (
      schedule.scheduleDate < filter.dateRange.start ||
      schedule.scheduleDate > filter.dateRange.end
    ) {
      return false;
    }

    return true;
  });

  /**
   * 承認対象（登録状態）のスケジュールのみを取得
   */
  const approvalSchedules = filteredSchedules.filter(s => s.status === 'registered');

  /**
   * フィルター変更ハンドラー
   */
  const handleFilterChange = (newFilter: ScheduleFilter) => {
    setFilter(newFilter);
  };

  /**
   * 前週に移動
   */
  const handlePreviousWeek = () => {
    const prevDate = new Date(weekStart);
    prevDate.setDate(prevDate.getDate() - 7);
    setWeekStart(prevDate.toISOString().split('T')[0]);
  };

  /**
   * 次週に移動
   */
  const handleNextWeek = () => {
    const nextDate = new Date(weekStart);
    nextDate.setDate(nextDate.getDate() + 7);
    setWeekStart(nextDate.toISOString().split('T')[0]);
  };

  /**
   * 個別承認
   */
  const handleApprove = (scheduleId: string, comment?: string) => {
    onScheduleUpdate(scheduleId, {
      status: 'confirmed',
      notes: comment ? `（承認）${comment}` : undefined,
    });
  };

  /**
   * 個別キャンセル
   */
  const handleCancel = (scheduleId: string, reason: string) => {
    onScheduleUpdate(scheduleId, {
      status: 'cancelled',
      notes: `（キャンセル）${reason}`,
    });
  };

  /**
   * スケジュール選択（詳細画面へ遷移する場合）
   */
  const handleSelectSchedule = (scheduleId: string) => {
    // 詳細画面への遷移や編集モーダル表示を実装
    // TODO: Implement schedule detail navigation
    void scheduleId; // Suppress unused parameter warning
  };

  /**
   * 新規スケジュール追加
   */
  const handleAddSchedule = (driverId: string, date: string) => {
    // 新規スケジュール作成ダイアログの表示など
    // TODO: Implement add schedule dialog
    void driverId;
    void date;
  };

  // 表示対象のドライバー
  const displayedDrivers =
    filter.driverIds.length > 0 ? drivers.filter(d => filter.driverIds.includes(d.id)) : drivers;

  // ドライバー名マップ
  const driverNameMap = drivers.reduce(
    (acc, driver) => {
      acc[driver.id] = driver.name;
      return acc;
    },
    {} as Record<string, string>
  );

  return (
    <div
      data-testid='driver-schedule-manager'
      className='space-y-4 p-4 bg-gray-50 dark:bg-gray-900 min-h-screen'
    >
      {/* ページタイトル */}
      <div>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
          ドライバースケジュール管理
        </h1>
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          複数ドライバーのスケジュール確認・承認
        </p>
      </div>

      {/* メインコンテンツ */}
      <div className='grid grid-cols-1 lg:grid-cols-4 gap-4'>
        {/* 左サイドバー: フィルターパネル */}
        <div className='lg:col-span-1'>
          <ScheduleFilterPanel
            drivers={drivers}
            onFilterChange={handleFilterChange}
            initialFilter={filter}
          />
        </div>

        {/* メイン: スケジュール表示 + 承認パネル */}
        <div className='lg:col-span-3 space-y-4'>
          {/* 週間ナビゲーション */}
          <div className='bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700'>
            <div className='flex items-center justify-between'>
              <div>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  スケジュール表示
                </h2>
                <p className='text-sm text-gray-600 dark:text-gray-400'>{weekCount}週間表示</p>
              </div>

              <div className='flex gap-2'>
                <button
                  onClick={handlePreviousWeek}
                  className='px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  data-testid='previous-week-button'
                >
                  ← 前週
                </button>
                <button
                  onClick={handleNextWeek}
                  className='px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  data-testid='next-week-button'
                >
                  次週 →
                </button>
              </div>
            </div>
          </div>

          {/* 複数週スケジュール表示 */}
          <MultiWeekScheduleDisplay
            weekStart={weekStart}
            weekCount={weekCount}
            schedules={filteredSchedules}
            drivers={displayedDrivers}
            onSelectSchedule={handleSelectSchedule}
            onAddSchedule={handleAddSchedule}
          />

          {/* 承認パネル */}
          <ScheduleApprovalPanel
            schedules={approvalSchedules}
            onApprove={handleApprove}
            onCancel={handleCancel}
            onBatchApprove={onBatchApprove}
            onBatchCancel={onBatchCancel}
            driverNameMap={driverNameMap}
          />
        </div>
      </div>
    </div>
  );
};

export default DriverScheduleManager;
